import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Form, Input, InputNumber, message, Modal, Popconfirm,
  Space, Switch, Table, Tag, Tooltip, Typography
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
  LockOutlined
} from '@ant-design/icons';
import { supplierGroupApi } from './supplierApi';
import './SupplierModal.css';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────
// Glass-i — Supplier Group CRUD popup (Stage 6B.3B).
//
// Centered AntD Modal launched from the Supplier Master toolbar.
// Inline edit / add form sits ABOVE the table; clicking "Edit" on a
// row loads it into the form. System rows are read-only (locked) on
// delete; their is_active toggle is allowed only when no suppliers
// are linked (the backend enforces the gate — we surface a friendly
// message and refresh on 409).
//
// Form layout uses the shared `.erp-supplier-modal` chrome (light
// title band, slate-50 footer) and `.erp-supplier-drawer__form` for
// the compact form rhythm.
// ─────────────────────────────────────────────────────────────────

const initialForm = () => ({
  groupCode:            '',
  groupName:            '',
  description:          '',
  defaultCreditDays:    0,
  defaultPaymentTerms: '',
  isActive:             true,
  sortOrder:            null
});

function unwrapApiError(err, fallback) {
  return err?.response?.data?.error?.message || err?.message || fallback;
}

export default function SupplierGroupsModal({ open, onClose, onChanged }) {
  const [form] = Form.useForm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [busySave, setBusySave] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await supplierGroupApi.list({
        pageSize: 200, includeInactive: includeInactive ? 'true' : 'false'
      });
      setRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch (e) {
      message.error(unwrapApiError(e, 'Failed to load supplier groups.'));
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue(initialForm());
    setEditingId(null);
    refresh();
  }, [open, form, refresh]);

  const startEdit = useCallback((record) => {
    setEditingId(record.id);
    form.setFieldsValue({
      groupCode:            record.groupCode || '',
      groupName:            record.groupName || '',
      description:          record.description || '',
      defaultCreditDays:    Number.isFinite(record.defaultCreditDays) ? record.defaultCreditDays : 0,
      defaultPaymentTerms:  record.defaultPaymentTerms || '',
      isActive:             record.isActive !== false,
      sortOrder:            record.sortOrder ?? null
    });
  }, [form]);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue(initialForm());
  }, [form]);

  const submitForm = useCallback(async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (_e) {
      return;
    }
    setBusySave(true);
    try {
      const payload = {
        ...values,
        groupCode: String(values.groupCode || '').toUpperCase().trim(),
        groupName: String(values.groupName || '').trim(),
        defaultPaymentTerms: values.defaultPaymentTerms
          ? String(values.defaultPaymentTerms).trim()
          : null,
        description: values.description ? String(values.description).trim() : null,
        sortOrder: values.sortOrder ?? null
      };
      if (editingId) {
        await supplierGroupApi.update(editingId, payload);
        message.success('Supplier group updated.');
      } else {
        await supplierGroupApi.create(payload);
        message.success('Supplier group created.');
      }
      cancelEdit();
      await refresh();
      onChanged && onChanged();
    } catch (e) {
      message.error(unwrapApiError(e, 'Failed to save supplier group.'));
    } finally {
      setBusySave(false);
    }
  }, [form, editingId, cancelEdit, refresh, onChanged]);

  const onDelete = useCallback(async (record) => {
    try {
      await supplierGroupApi.remove(record.id);
      message.success('Supplier group deleted.');
      if (editingId === record.id) cancelEdit();
      await refresh();
      onChanged && onChanged();
    } catch (e) {
      // Friendly inline message instead of a stuck modal — backend
      // returns 409 with a count when active suppliers are linked.
      message.error(unwrapApiError(e, 'Failed to delete supplier group.'));
    }
  }, [editingId, cancelEdit, refresh, onChanged]);

  const onToggleActive = useCallback(async (record, nextActive) => {
    try {
      await supplierGroupApi.update(record.id, { isActive: nextActive });
      message.success(nextActive ? 'Group activated.' : 'Group deactivated.');
      await refresh();
      onChanged && onChanged();
    } catch (e) {
      message.error(unwrapApiError(e, 'Failed to update supplier group.'));
    }
  }, [refresh, onChanged]);

  const columns = useMemo(() => ([
    { title: 'Code', dataIndex: 'groupCode', key: 'groupCode', width: 170,
      render: (v, r) => (
        <Space size={4}>
          <code style={{ fontSize: 12 }}>{v}</code>
          {r.isSystem ? (
            <Tooltip title="System-seeded group (cannot be hard-deleted)">
              <LockOutlined style={{ fontSize: 11, color: '#94a3b8' }} />
            </Tooltip>
          ) : null}
        </Space>
      ) },
    { title: 'Name', dataIndex: 'groupName', key: 'groupName', ellipsis: true },
    { title: 'Credit Days', dataIndex: 'defaultCreditDays', key: 'defaultCreditDays',
      width: 110, align: 'right',
      render: (v) => Number.isFinite(Number(v)) ? Number(v) : 0 },
    { title: 'Linked', dataIndex: 'supplierCount', key: 'supplierCount',
      width: 90, align: 'right',
      render: (v) => v > 0
        ? <Tag color="blue" style={{ marginInlineEnd: 0 }}>{v}</Tag>
        : <Text type="secondary">—</Text> },
    { title: 'Active', dataIndex: 'isActive', key: 'isActive', width: 90, align: 'center',
      render: (v, r) => (
        <Switch size="small" checked={v === true}
          onChange={(c) => onToggleActive(r, c)} />
      ) },
    { title: '', key: '__actions__', width: 110, align: 'right',
      render: (_v, r) => (
        <Space size={4}>
          <Tooltip title="Edit">
            <Button size="small" type="text" icon={<EditOutlined />}
              onClick={() => startEdit(r)} />
          </Tooltip>
          {r.isSystem ? (
            <Tooltip title="System group — cannot be deleted">
              <Button size="small" type="text" disabled icon={<DeleteOutlined />} />
            </Tooltip>
          ) : (
            <Popconfirm title={`Delete supplier group ${r.groupCode}?`}
              description="The group is soft-deleted. Active suppliers must be re-assigned first."
              okText="Delete" okButtonProps={{ danger: true }} cancelText="Keep"
              onConfirm={() => onDelete(r)}>
              <Button size="small" type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ) }
  ]), [startEdit, onDelete, onToggleActive]);

  return (
    <Modal
      title="Supplier Groups"
      open={open}
      onCancel={onClose}
      width={920}
      centered
      destroyOnClose
      maskClosable={false}
      className="erp-supplier-modal"
      styles={{ body: { maxHeight: '72vh', overflowY: 'auto', padding: 0 } }}
      footer={(
        <Space>
          <Button size="small" icon={<ReloadOutlined />} onClick={refresh}>Refresh</Button>
          <Button onClick={onClose}>Close</Button>
        </Space>
      )}
    >
      <div className="erp-supplier-drawer__form" style={{ padding: '12px 16px' }}>
        <Alert
          type="info" showIcon style={{ marginBottom: 10 }}
          message="Manage Supplier Groups"
          description="Structured category for suppliers. Linked groups cannot be deleted until their suppliers are re-assigned. System-seeded rows can be renamed or deactivated but never hard-deleted."
        />

        <Form form={form} layout="vertical" onFinish={submitForm}>
          <Space wrap size={8} style={{ width: '100%' }}>
            <Form.Item label="Code" name="groupCode" style={{ width: 200, marginBottom: 6 }}
              rules={[
                { required: true, message: 'Code is required' },
                { pattern: /^[A-Z0-9_-]+$/i, message: 'Allowed: A-Z, 0-9, _, -' },
                { min: 2, max: 40 }
              ]}
              normalize={(v) => (v ? String(v).toUpperCase() : v)}>
              <Input placeholder="RAW_GLASS_SUPPLIERS" />
            </Form.Item>
            <Form.Item label="Name" name="groupName" style={{ width: 280, marginBottom: 6 }}
              rules={[{ required: true, min: 2, max: 150, message: 'Name 2–150 chars' }]}>
              <Input placeholder="Raw Glass Suppliers" />
            </Form.Item>
            <Form.Item label="Default Credit Days" name="defaultCreditDays"
              style={{ width: 140, marginBottom: 6 }}
              rules={[{ type: 'number', min: 0, max: 3650 }]}>
              <InputNumber min={0} max={3650} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item label="Active" name="isActive" valuePropName="checked"
              style={{ width: 80, marginBottom: 6 }}>
              <Switch size="small" />
            </Form.Item>
            <Form.Item label="Sort" name="sortOrder" style={{ width: 90, marginBottom: 6 }}
              tooltip="Lower numbers appear first in dropdowns.">
              <InputNumber min={0} max={999999} style={{ width: '100%' }} />
            </Form.Item>
          </Space>
          <Form.Item label="Default Payment Terms" name="defaultPaymentTerms"
            style={{ marginBottom: 6 }}>
            <Input placeholder="e.g. 30 days net, 2% if paid in 10 days" maxLength={2000} />
          </Form.Item>
          <Form.Item label="Description" name="description" style={{ marginBottom: 8 }}>
            <Input.TextArea rows={2} maxLength={2000} placeholder="Optional description." />
          </Form.Item>
          <Space size="small">
            <Button type="primary" htmlType="submit" size="small" loading={busySave}
              icon={editingId ? <EditOutlined /> : <PlusOutlined />}>
              {editingId ? 'Update Group' : 'Add Group'}
            </Button>
            {editingId ? (
              <Button size="small" onClick={cancelEdit}>Cancel Edit</Button>
            ) : null}
          </Space>
        </Form>

        <Table
          size="small"
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={rows}
          pagination={false}
          style={{ marginTop: 12 }}
        />
      </div>
    </Modal>
  );
}
